import { Pizza } from '@/data/menu';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import GlutenFreeIcon from '@/components/GlutenFreeIcon';

interface PizzaCardProps {
  pizza: Pizza;
  showGlutenChoice: boolean;
  onToggleGlutenChoice: (show: boolean) => void;
  ordersDisabled?: boolean;
}

const PizzaCard = ({ pizza, showGlutenChoice, onToggleGlutenChoice, ordersDisabled }: PizzaCardProps) => {
  const { addToCart } = useCart();

  const handleAddToCart = (isGlutenFree: boolean) => {
    addToCart(pizza, isGlutenFree);
    onToggleGlutenChoice(false);
    toast.success(
      `${pizza.name} ${isGlutenFree ? '(senza glutine)' : ''} aggiunta al carrello!`,
      {
        duration: 2000,
      }
    );
  };

  return (
    <div className="card-elevated p-4 md:p-6 group transition-all duration-300 flex flex-col h-full">
      {/* Pizza Image - only when orders are enabled */}
      {!ordersDisabled && (
        <div className="relative aspect-square rounded-lg overflow-hidden mb-4 bg-muted">
          <div
            className="absolute inset-0 flex items-center justify-center"
            role="img"
            aria-label={`Pizza ${pizza.name}`}
          >
            <span className="text-6xl opacity-50" aria-hidden="true">🍕</span>
          </div>
          {(pizza.hasSeafood || pizza.hasGlutenTraces) && (
            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/30">
              <AlertTriangle className="h-3 w-3 text-yellow-400" />
              <span className="text-xs text-yellow-400">Tracce di glutine</span>
            </div>
          )}
        </div>
      )}

      {/* Pizza Info */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-xl font-semibold text-foreground transition-colors">
            {pizza.name}
          </h3>
          <span className="text-primary font-bold text-lg whitespace-nowrap">
            €{pizza.price.toFixed(2)}
          </span>
        </div>

        {/* Ingredients */}
        <p className="text-sm text-muted-foreground mb-4 flex-1">
          {pizza.ingredients.join(', ')}
        </p>

        {/* Allergen badge - shown inline when image is hidden */}
        {ordersDisabled && (pizza.hasSeafood || pizza.hasGlutenTraces) && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/30 w-fit mb-4">
            <AlertTriangle className="h-3 w-3 text-yellow-400" />
            <span className="text-xs text-yellow-400">Tracce di glutine</span>
          </div>
        )}

        {/* Add to Cart */}
        {!ordersDisabled && (!showGlutenChoice ? (
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => onToggleGlutenChoice(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi al Carrello
          </Button>
        ) : (
          <div className="space-y-2 animate-fade-in">
            <p className="text-sm text-muted-foreground text-center mb-2">Scegli il tipo di impasto:</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddToCart(false)}
                className="text-xs"
              >
                Con Glutine
              </Button>
              <Button
                variant="glutenFree"
                size="sm"
                onClick={() => handleAddToCart(true)}
                className="text-xs"
              >
                <GlutenFreeIcon className="h-3 w-3 mr-1" />
                Senza Glutine
              </Button>
            </div>
            <button
              onClick={() => onToggleGlutenChoice(false)}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              Annulla
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PizzaCard;
